import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, TextInput, View } from 'react-native';
import useNeutralTheme from '@/hooks/useNeutralTheme';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { observeTextareaWidth, resizeComposerTextarea } from '@/components/ChatInput/textareaLayout';
import { isComposerEnterEvent } from '@/constants/utils/composerEnter';

export default function BotSetupComposer({ value, onChangeText, onSend, busy, maxLength, label, placeholder, maxInputHeight = 240 }) {
  const { colors } = useNeutralTheme();
  const [focused, setFocused] = useState(false);
  const input = useRef(null);
  const [height, setHeight] = useState(46);
  const disabled = busy || !value.trim();
  useEffect(() => {
    if (Platform.OS === 'web' && input.current) resizeComposerTextarea(input.current);
  }, [value]);
  useEffect(() => {
    if (Platform.OS !== 'web' || !input.current) return undefined;
    return observeTextareaWidth(input.current, () => resizeComposerTextarea(input.current));
  }, []);
  return <View style={{
    borderRadius: 12, borderWidth: focused ? 2 : 1,
    borderColor: focused ? colors.text_color_primary : colors.border_color_primary,
    backgroundColor: colors.solid_color_primary, padding: focused ? 0 : 1,
    overflow: 'hidden', minHeight: 96,
  }}>
    <TextInput ref={input} value={value} onChangeText={onChangeText} maxLength={maxLength}
      editable={!busy} multiline accessibilityLabel={label} placeholder={placeholder}
      placeholderTextColor={colors.text_color_secondary}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      style={{ color: colors.text_color_primary, backgroundColor: 'transparent', fontSize: 17, lineHeight: 24,
        paddingVertical: 12, paddingLeft: 16, paddingRight: 16, minHeight: 46, maxHeight: maxInputHeight,
        borderWidth: 0, textAlignVertical: 'top',
        ...(Platform.OS === 'web' ? { outlineStyle: 'none', resize: 'none' } : { height: Math.min(height, maxInputHeight) }),
      }}
      onContentSizeChange={Platform.OS !== 'web' ? e => setHeight(Math.max(46, e.nativeEvent.contentSize.height)) : undefined}
      onKeyPress={Platform.OS === 'web' ? e => {
        if (isComposerEnterEvent(e) && !e.shiftKey && !e.nativeEvent?.shiftKey) {
          e.preventDefault();
          if (!disabled) onSend();
        }
      } : undefined}
    />
    <View style={{ flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', paddingHorizontal: 8, paddingBottom: 8 }}>
      <Pressable {...(Platform.OS === 'ios' || Platform.OS === 'android' ? { onPressIn: onSend } : { onPress: onSend })}
        disabled={disabled} accessibilityRole="button" accessibilityLabel="Continue" accessibilityState={{ disabled, busy }}
        style={({ hovered, pressed }) => ({ minHeight: 44, paddingHorizontal: 16, borderRadius: 8, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: colors.text_color_primary, opacity: disabled ? 0.35 : hovered || pressed ? 0.85 : 1 })}>
        {busy ? <ActivityIndicator size="small" color={colors.accent_text_color} /> : <>
          <Text size="s" weight="medium" style={{ color: colors.accent_text_color }}>Continue</Text>
          <Icon name="arrow-right" size={12} color={colors.accent_text_color} />
        </>}
      </Pressable>
    </View>
  </View>;
}
